import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Icono, Avatar } from "./iconos";
import { defaultFilters, fetchCampaignOptions, fetchDashboard } from "./dashboardApi";
import {
    ZMLayout,
    ZMCard,
    ZMMetricCard,
    ZMTable,
    ZMBadge,
    ZMFiltersBar,
    ZMEmptyState,
    ZMLoadingState,
    ZMErrorState,
} from "./components/ZMUI";

const COLUMNAS = [
    { key: "nuevo", label: "Nuevo" },
    { key: "contactado", label: "Contactado" },
    { key: "calificado", label: "Calificado" },
    { key: "demo", label: "Demo" },
    { key: "cerrado", label: "Cerrado" },
    { key: "rechazado", label: "Rechazado" },
];

const fmtNum = (n, dec = 0) =>
    n == null ? "0" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtPct = (n) => `${fmtNum(n || 0, 2)}%`;

export default function ZMLeads() {
    const { darkMode } = useContext(DarkModeContext);
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [cargandoFiltros, setCargandoFiltros] = useState(false);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState(() => defaultFilters());
    const [draftFilters, setDraftFilters] = useState(() => defaultFilters());
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [datos, setDatos] = useState({
        leads: [],
        kanban: {},
        metricas: {},
        auditoria: {},
        leadsPrioritarios: [],
        accionesSugeridas: [],
    });

    const cargar = async (nextFilters = filters, withLoader = false) => {
        const token = getToken();
        if (!token) {
            navigate("/");
            return;
        }

        if (withLoader) setCargandoFiltros(true);
        setError("");
        try {
            const [payload, optionItems] = await Promise.all([
                fetchDashboard("leads-crm", token, nextFilters),
                fetchCampaignOptions(token, nextFilters),
            ]);
            setDatos(payload || {});
            setCampaignOptions(optionItems);
        } catch (err) {
            if (err?.code === 401 || err?.code === 403 || err?.message === "unauthorized") {
                navigate("/");
                return;
            }
            setError("No se pudo cargar Leads CRM.");
        } finally {
            setCargando(false);
            setCargandoFiltros(false);
        }
    };

    useEffect(() => {
        cargar(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const metricas = datos.metricas || {};
    const auditoria = datos.auditoria || {};
    const ultimosLeads = useMemo(
        () => [...(datos.leads || [])].sort((a, b) => new Date(b.fechaRecibido) - new Date(a.fechaRecibido)).slice(0, 8),
        [datos.leads]
    );

    const gestionarLead = (leadId) => {
        if (!leadId) return;
        navigate(`/zocometa/carga?leadId=${leadId}`);
    };

    if (cargando) return <ZMLoadingState label="Cargando Leads CRM..." fullScreen />;
    if (error) return <ZMErrorState message={error} onRetry={() => cargar(filters)} />;

    return (
        <ZMLayout
            title="Leads y CRM"
            subtitle="Kanban comercial, seguimiento y prioridades operativas con datos reales deduplicados."
            right="Datos en tiempo real"
            className={darkMode ? "container-dark" : ""}
        >
            <ZMFiltersBar className="mb-3">
                <div>
                    <label>Desde</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaDesde || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaDesde: e.target.value }))} />
                </div>
                <div>
                    <label>Hasta</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaHasta || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaHasta: e.target.value }))} />
                </div>
                <div style={{ minWidth: 240 }}>
                    <label>Campaña</label>
                    <select className="form-select form-select-sm" value={draftFilters.campania || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, campania: e.target.value }))}>
                        <option value="">Todas las campañas</option>
                        {campaignOptions.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                    </select>
                </div>
                <div>
                    <label>Estado</label>
                    <select className="form-select form-select-sm" value={draftFilters.estado || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, estado: e.target.value }))}>
                        <option value="">Todos los estados</option>
                        {COLUMNAS.map((col) => <option key={col.key} value={col.key}>{col.label}</option>)}
                    </select>
                </div>
                <div style={{ minWidth: 240 }}>
                    <label>Buscar</label>
                    <input className="form-control form-control-sm" value={draftFilters.buscar || ""} placeholder="Lead, campaña, anuncio..." onChange={(e) => setDraftFilters((p) => ({ ...p, buscar: e.target.value }))} />
                </div>
                <div>
                    <button type="button" className="btn btn-sm zm-btn-primary" onClick={() => {
                        setFilters({ ...draftFilters });
                        cargar({ ...draftFilters }, true);
                    }}>
                        {cargandoFiltros ? "Cargando..." : "Aplicar"}
                    </button>
                </div>
            </ZMFiltersBar>

            <div className="zm-grid-metrics">
                <ZMMetricCard icon={<Icono.UserPlus />} label="Leads nuevos" value={fmtNum(metricas.nuevos, 0)} />
                <ZMMetricCard icon={<Icono.Phone />} label="Contactados" value={fmtNum(metricas.contactados, 0)} />
                <ZMMetricCard icon={<Icono.Star />} label="Calificados" value={fmtNum(metricas.calificados, 0)} />
                <ZMMetricCard icon={<Icono.CheckCircle />} label="Cerrados" value={fmtNum(metricas.cerrados, 0)} />
                <ZMMetricCard icon={<Icono.Target />} label="Tasa contacto" value={fmtPct(metricas.tasaContacto)} />
                <ZMMetricCard icon={<Icono.Clock />} label="Tiempo medio" value={metricas.tiempoMedioRespuestaMinutos ? `${fmtNum(metricas.tiempoMedioRespuestaMinutos, 1)} min` : "sin datos"} />
                <ZMMetricCard icon={<Icono.CheckCircle />} label="Leads únicos" value={fmtNum(auditoria.leadsUnicos, 0)} helper="No cuentan doble en CRM" />
                <ZMMetricCard icon={<Icono.AlertTriangle />} label="Duplicados" value={fmtNum(auditoria.leadsDuplicados, 0)} helper="Detectados por id/teléfono/email" />
            </div>

            {(datos.leads || []).length === 0 ? (
                <ZMEmptyState title="Sin leads para mostrar" hint="No hubo coincidencias con los filtros aplicados." />
            ) : (
                <>
                    <ZMCard title="Kanban comercial" subtitle="Estados reales cruzados con ficha del closer" className="mb-3">
                        <div className="zm-kanban">
                            {COLUMNAS.map((col) => {
                                const leadsCol = Array.isArray(datos.kanban?.[col.key]) ? datos.kanban[col.key] : [];
                                return (
                                    <div key={col.key} className="zm-kanban-col">
                                        <div className="zm-kanban-head">
                                            <div>
                                                <strong>{col.label}</strong>
                                                <span>{leadsCol.length} leads</span>
                                            </div>
                                            <ZMBadge status={col.key} />
                                        </div>
                                        <div className="zm-kanban-body">
                                            {leadsCol.length === 0 ? (
                                                <div className="zm-kanban-empty">Sin leads</div>
                                            ) : (
                                                leadsCol.slice(0, 6).map((lead) => (
                                                    <div key={lead.id} className="zm-kanban-card">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <Avatar nombre={lead.nombre} size={34} />
                                                            <div className="flex-grow-1">
                                                                <strong>{lead.nombre || "Sin nombre"}</strong>
                                                                <div>{lead.campaignName || lead.campaignId || "Sin campaña"}</div>
                                                            </div>
                                                        </div>
                                                        <small>{lead.formName || "Sin formulario"} · {lead.adName || "Sin anuncio"}</small>
                                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                                            <ZMBadge status={lead.estado} />
                                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => gestionarLead(lead.id)}>Gestionar</button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </ZMCard>

                    <div className="row g-3">
                        <div className="col-12 col-xl-8">
                            <ZMCard title="Últimos leads recibidos">
                                <ZMTable
                                    emptyText="Sin leads recientes."
                                    columns={[
                                        { key: "fechaRecibido", header: "Fecha", render: (lead) => new Date(lead.fechaRecibido).toLocaleString("es-AR") },
                                        { key: "nombre", header: "Nombre", render: (lead) => lead.nombre || "-" },
                                        { key: "campaignName", header: "Campaña", render: (lead) => lead.campaignName || lead.campaignId || "-" },
                                        { key: "telefono", header: "Teléfono", render: (lead) => lead.telefono || "-" },
                                        { key: "estado", header: "Estado", render: (lead) => <ZMBadge status={lead.estado} /> },
                                        { key: "accion", header: "Acción", render: (lead) => <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => gestionarLead(lead.id)}>Gestionar</button> },
                                    ]}
                                    rows={ultimosLeads}
                                />
                            </ZMCard>
                        </div>
                        <div className="col-12 col-xl-4">
                            <ZMCard title="Acciones sugeridas" subtitle="Prioridades del día">
                                {(datos.accionesSugeridas || []).length === 0 ? (
                                    <ZMEmptyState title="Sin acciones urgentes" hint="No hay leads vencidos ni focos prioritarios para este rango." />
                                ) : (
                                    <div className="zm-alert-stack">
                                        {(datos.accionesSugeridas || []).map((item, index) => (
                                            <div key={`${item.leadId}-${index}`} className="zm-alert-item">
                                                <div className="d-flex justify-content-between gap-2">
                                                    <div>
                                                        <strong>{item.lead || "Lead"}</strong>
                                                        <p>{item.motivo}</p>
                                                        <small>{item.campaña || item.campaignName || item.campaignId || "Sin campaña"}</small>
                                                    </div>
                                                    <ZMBadge
                                                        status={item.prioridad === "alta" ? "rechazado" : item.prioridad === "media" ? "contactado" : "cerrado"}
                                                        label={item.prioridad}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </ZMCard>
                        </div>
                    </div>

                    <div className="row g-3 mt-1">
                        <div className="col-12">
                            <ZMCard title="Leads prioritarios" subtitle="Sin contacto o vinculados a campañas con cierres">
                                <ZMTable
                                    emptyText="Sin leads prioritarios."
                                    columns={[
                                        { key: "nombre", header: "Lead", render: (lead) => lead.nombre || "-" },
                                        { key: "campaignName", header: "Campaña", render: (lead) => lead.campaignName || lead.campaignId || "-" },
                                        { key: "estado", header: "Estado", render: (lead) => <ZMBadge status={lead.estado} /> },
                                        { key: "horasSinContacto", header: "Horas sin contacto", cellClassName: "text-end", render: (lead) => fmtNum(lead.horasSinContacto, 1) },
                                        { key: "motivo", header: "Motivo" },
                                    ]}
                                    rows={datos.leadsPrioritarios || []}
                                />
                            </ZMCard>
                        </div>
                    </div>
                </>
            )}
        </ZMLayout>
    );
}
